// Phase 14 — Ownership transfer inbox.
// Lists PENDING transfers where the current user is the recipient.
// Supports deep-link: /transfer-inbox?transfer_id=...&token=...
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { listPendingTransfers, acceptTransfer, declineTransfer } from '@/lib/pro-toolkit-api';

export default function TransferInboxPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const deepLinkToken = searchParams.get('token') || undefined;
  const deepLinkTransferId = searchParams.get('transfer_id') || undefined;

  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(null);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listPendingTransfers();
      setTransfers(res.transfers || []);
    } catch (e) {
      setError(e?.message || 'Could not load pending transfers.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function onAccept(t) {
    setActionBusy(t.id);
    try {
      await acceptTransfer(t.id, deepLinkTransferId === t.id ? deepLinkToken : undefined);
      setFlash({ type: 'success', text: `You're now the host of "${t.event_name || 'the event'}".` });
      setTimeout(() => navigate('/host-dashboard', { replace: true }), 1800);
    } catch (e) {
      setFlash({ type: 'error', text: e?.message || 'Could not accept. Please try again.' });
    } finally {
      setActionBusy(null);
    }
  }

  async function onDecline(t) {
    setActionBusy(t.id);
    try {
      await declineTransfer(t.id, deepLinkTransferId === t.id ? deepLinkToken : undefined);
      await load();
    } catch (e) {
      setFlash({ type: 'error', text: e?.message || 'Could not decline. Please try again.' });
    } finally {
      setActionBusy(null);
    }
  }

  // Auto-dismiss flash
  useEffect(() => {
    if (!flash) return;
    const timer = setTimeout(() => setFlash(null), 4000);
    return () => clearTimeout(timer);
  }, [flash]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="transfer-inbox-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <button
          onClick={() => navigate(-1)}
          className="p-1 rounded hover:bg-[#F3F5F8] transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
          data-testid="transfer-inbox-back-btn"
          aria-label="Go back"
        >
          <ChevronLeft className="w-5 h-5 text-[#0B1B46]" />
        </button>
        <h1 className="text-xl font-extrabold text-[#0B1B46]" data-testid="transfer-inbox-title">Event transfers</h1>
        <div className="w-5" />
      </div>
      <p className="text-sm text-[#5B6472] leading-5 mb-4">
        {user?.first_name ? `Hi ${user.first_name}. ` : ''}
        These events are offered to you as host. Accept to take ownership; decline to leave the current host in place.
      </p>

      {/* Flash toast */}
      {flash && (
        <div className={`mb-4 px-4 py-2.5 rounded-lg text-sm font-semibold transition-opacity ${
          flash.type === 'success' ? 'bg-[#E4F4EC] text-[#1F8A55]' : 'bg-[#FEF2F2] text-[#DC2626]'
        }`} data-testid="transfer-inbox-flash">
          {flash.text}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex justify-center py-10" data-testid="transfer-inbox-loading">
          <div className="w-7 h-7 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-5">
          <p className="text-[13px] font-semibold text-[#DC2626]" data-testid="transfer-inbox-error">{error}</p>
        </div>
      ) : transfers.length === 0 ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-5" data-testid="transfer-inbox-empty">
          <p className="text-[11px] font-extrabold text-[#0B1B46] uppercase tracking-wide mb-1">No pending transfers</p>
          <p className="text-sm text-[#5B6472]">You&apos;ll see any incoming event ownership requests here.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3 pb-6">
          {transfers.map((t) => (
            <div
              key={t.id}
              className="bg-white border border-[#E3E8EF] rounded-xl p-4"
              data-testid={`transfer-inbox-card-${t.id}`}
            >
              <p className="text-base font-extrabold text-[#0B1B46]" data-testid={`transfer-inbox-name-${t.id}`}>
                {t.event_name || 'Untitled event'}
              </p>
              <p className="text-xs text-[#5B6472] mt-1">
                Expires {new Date(t.expires_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                {t.keep_as_cohost ? ' \u00B7 Current host stays as Co-Host' : ' \u00B7 Current host steps off'}
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => onAccept(t)}
                  disabled={actionBusy !== null}
                  className="flex-1 h-10 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
                  data-testid={`transfer-inbox-accept-${t.id}`}
                >
                  {actionBusy === t.id ? (
                    <span className="inline-flex items-center gap-1">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Accepting...
                    </span>
                  ) : 'Accept'}
                </button>
                <button
                  onClick={() => onDecline(t)}
                  disabled={actionBusy !== null}
                  className="flex-1 h-10 bg-white border border-[#E3E8EF] hover:bg-[#F3F5F8] disabled:opacity-50 text-[#0B1B46] font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
                  data-testid={`transfer-inbox-decline-${t.id}`}
                >
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
